import { z } from "zod";

import {
  IsoTimestampSchema,
  LocaleSchema,
  LocalizedTextSchema,
  MoneyHalalasSchema,
  PaginationQuerySchema,
  PhoneSchema,
  ReturnReferenceSchema,
  UploadIdSchema,
} from "./common";
import { OrderSummarySchema, PublicOrderSchema } from "./order";
import {
  CollectionMethodSchema,
  LineEligibilitySchema,
  MerchantPolicySchema,
  MerchantThemeSchema,
  PolicyViolationSchema,
  PublicPolicySchema,
  ResolutionTypeSchema,
  ReturnReasonCodeSchema,
} from "./policy";
import {
  DispositionSchema,
  GradeSchema,
  LineStateSchema,
  ReturnEventSchema,
  ReturnLineSchema,
  ReturnOrderSchema,
  ShopperEventSchema,
  TimelineStepSchema,
} from "./return";
import { ListReturnsSortSchema, MerchantStateGroupSchema } from "./state-group";

/**
 * docs/06 — request/response envelopes. Machine code plus human-facing bilingual text,
 * always kept separate.
 */

export const ApiErrorCodeSchema = z.enum([
  "order_not_found",
  "customer_blocked",
  "returns_disabled",
  "policy_violation",
  "rate_limited",
  "not_found",
  "invalid_request",
  "internal_error",
  "merchant_exists",
  /** The evidence upload endpoint refused the file: wrong type, too large, or unreadable. */
  "invalid_photo",
  /**
   * The merchant is still on the temporary password Sirdab issued. Every settings endpoint
   * except `auth/*` refuses with this until they choose their own, so the change screen
   * cannot be skipped by deep-linking past it.
   */
  "password_change_required",
  /**
   * Sign-in refused. Deliberately returned for a wrong password, an unknown merchant, a
   * merchant with no password set, and a suspended merchant alike — the four are
   * byte-identical, and an unknown merchant still pays a scrypt derivation so timing does
   * not separate them either.
   */
  "invalid_credentials",
  /** Too many failed sign-in attempts; the account is locked for a cooling-off period. */
  "too_many_attempts",
  /** Sirdab has suspended this merchant. Distinct from the merchant closing returns themselves. */
  "merchant_suspended",
  /**
   * The one-time code is wrong, expired, used up, or the shopper token is not live. One
   * code for all of them, so a caller learns nothing about which codes were ever issued.
   */
  "invalid_code",
  /**
   * The return moved between the caller reading it and writing to it: a second merchant
   * approved first, a webhook landed, a sweep booked the pickup. Nothing was written.
   * Re-read and decide again from the current state.
   */
  "transition_conflict",
  /**
   * The shopper asked to cancel a return that has already moved past the point where it
   * can be withdrawn (a line is collected, received or settled). Nothing was changed; the
   * tracking view on screen is stale and should be re-read.
   */
  "cancel_not_allowed",
  /**
   * The Rails backend's shared rack_attack 429 body. Shopper paths answer `rate_limited`,
   * but a throttle that falls through to the shared responder says this instead, and the
   * shopper must still read "too many attempts" rather than a contract failure.
   */
  "too_many_requests",
]);
export type ApiErrorCode = z.infer<typeof ApiErrorCodeSchema>;

export const ApiErrorSchema = z.object({
  error: ApiErrorCodeSchema,
  /** Renders to the shopper. Never a bare code with no explanation (CLAUDE.md rule 5). */
  reasonText: LocalizedTextSchema.optional(),
  violations: z.array(PolicyViolationSchema).optional(),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/* ------------------------------------------------------------------ */
/* Public — shopper portal, unauthenticated                            */
/* ------------------------------------------------------------------ */

/**
 * `GET /api/public/merchants/:slug` — merchant context keyed on the public slug ALONE.
 *
 * This is the endpoint that lets the portal show a closed-store message without first
 * having to guess a valid order reference. That asymmetry is the whole point (docs/06): a
 * merchant's returns being switched off is not a secret, but a valid order reference is,
 * and making the portal produce one just to learn the store is closed turned the lookup
 * endpoint into an enumeration oracle.
 *
 * So this response is deliberately kept free of anything order-shaped — no order, no line,
 * no customer, no window, no reason list, nothing that varies per shopper. Everything here
 * is already public storefront branding. Resist adding policy detail to it: the moment this
 * answer differs per shopper, the oracle is back.
 */
export const StorefrontResponseSchema = z.object({
  slug: z.string().min(1),
  /** `MerchantPolicy.enabled`. False means the portal renders `portal.closed` and stops. */
  returnsEnabled: z.boolean(),
  theme: MerchantThemeSchema.optional(),
  localeDefault: LocaleSchema.optional(),
});
export type StorefrontResponse = z.infer<typeof StorefrontResponseSchema>;

export const LookupRequestSchema = z.object({
  merchantSlug: z.string().min(1),
  orderRef: z.string().min(1),
  /**
   * Optional when the request carries `Authorization: Bearer <shopperToken>` from
   * `/shopper/verify`: the verified phone comes from the session instead. Required
   * otherwise. Missing on both sides is a 400.
   */
  phone: PhoneSchema.optional(),
  locale: LocaleSchema.optional(),
});
export type LookupRequest = z.infer<typeof LookupRequestSchema>;

export const LookupResponseSchema = z.object({
  order: PublicOrderSchema,
  /** Ineligible lines are present and greyed, never filtered out (ADR-007). */
  lines: z.array(LineEligibilitySchema),
  policy: PublicPolicySchema,
});
export type LookupResponse = z.infer<typeof LookupResponseSchema>;

/* ----------------------------------------------------- shopper sign-in (SAI-1344) */

/**
 * A shopper proves a phone with a one-time code and gets back the orders that phone
 * placed with the merchant, so nobody has to find an order number. Everything here is
 * deliberately shaped so an unknown phone answers exactly like a known one.
 */
export const ShopperCodeRequestSchema = z.object({
  merchantSlug: z.string().min(1),
  phone: PhoneSchema,
  locale: LocaleSchema.optional(),
});
export type ShopperCodeRequest = z.infer<typeof ShopperCodeRequestSchema>;

/** The same body whether or not the phone owns any orders. */
export const ShopperCodeResponseSchema = z.object({
  codeLength: z.number().int().positive(),
  expiresInSeconds: z.number().int().positive(),
});
export type ShopperCodeResponse = z.infer<typeof ShopperCodeResponseSchema>;

export const ShopperVerifyRequestSchema = z.object({
  merchantSlug: z.string().min(1),
  phone: PhoneSchema,
  code: z.string().regex(/^\d{4,8}$/),
});
export type ShopperVerifyRequest = z.infer<typeof ShopperVerifyRequestSchema>;

export const ShopperVerifyResponseSchema = z.object({
  /** Bearer for `/shopper/orders` and `/lookup`. Held in memory by the portal, never stored. */
  shopperToken: z.string().min(1),
  expiresAt: IsoTimestampSchema,
});
export type ShopperVerifyResponse = z.infer<typeof ShopperVerifyResponseSchema>;

export const ShopperOrdersRequestSchema = z.object({
  merchantSlug: z.string().min(1),
});
export type ShopperOrdersRequest = z.infer<typeof ShopperOrdersRequestSchema>;

/**
 * One row of the shopper's own order list, with the merchant's policy already applied to
 * it. The adapter supplies the order; the API adds the verdict, because a store port
 * knows nothing about return windows.
 *
 * A blocked order is still LISTED, with its reason (ADR-007, same rule as an ineligible
 * line): an order that silently vanished would send the shopper to the store instead.
 */
export const ShopperOrderSchema = OrderSummarySchema.extend({
  /** Advisory. The engine decides again, line by line, when the order is opened. */
  returnable: z.boolean(),
  /** Why not, bilingual and server-authored. Null when `returnable`. */
  blockedReason: LocalizedTextSchema.nullable(),
});
export type ShopperOrder = z.infer<typeof ShopperOrderSchema>;

export const ShopperOrdersResponseSchema = z.object({
  orders: z.array(ShopperOrderSchema),
});
export type ShopperOrdersResponse = z.infer<typeof ShopperOrdersResponseSchema>;

export const CreateReturnLineInputSchema = z.object({
  lineRef: z.string().min(1),
  qty: z.number().int().positive(),
  reasonCode: ReturnReasonCodeSchema,
  subReason: z.string().optional(),
  note: z.string().optional(),
});
export type CreateReturnLineInput = z.infer<typeof CreateReturnLineInputSchema>;

// Defined in ./common so `./policy` can reference it too (the theme stores one); still
// exported from here, which is where every caller has always imported it from.
export { UploadIdSchema, type UploadId } from "./common";

export const CreateReturnEvidenceInputSchema = z.object({
  lineRef: z.string().min(1),
  uploadId: UploadIdSchema,
});
export type CreateReturnEvidenceInput = z.infer<
  typeof CreateReturnEvidenceInputSchema
>;

export const CreateReturnRequestSchema = z.object({
  merchantSlug: z.string().min(1),
  orderRef: z.string().min(1),
  phone: PhoneSchema,
  lines: z.array(CreateReturnLineInputSchema).min(1),
  /** A request, not an execution — Sirdab never moves money (ADR-003). */
  resolution: ResolutionTypeSchema,
  collectionMethod: CollectionMethodSchema,
  evidence: z.array(CreateReturnEvidenceInputSchema).optional(),
  locale: LocaleSchema,
});
export type CreateReturnRequest = z.infer<typeof CreateReturnRequestSchema>;

export const CreateReturnResponseSchema = z.object({
  reference: ReturnReferenceSchema,
  lines: z.array(
    z.object({ lineRef: z.string().min(1), state: LineStateSchema }),
  ),
  /** Resolved from the snapshot at creation, integer halalas. */
  feeApplied: MoneyHalalasSchema,
  /**
   * The Rails pack mints a track link with every return: `/:merchantSlug/returns/r/:token`
   * opens this return's tracking view without the phone. Returned once, here. Optional
   * and loose on purpose: the return already exists, so a link field must never turn
   * the success into a contract failure.
   */
  trackToken: z.string().min(1).optional(),
  trackExpiresAt: z.string().min(1).optional(),
});
export type CreateReturnResponse = z.infer<typeof CreateReturnResponseSchema>;

/**
 * A merchant uploading their own logo (SAI-1368): presign, then the browser PUTs. The
 * shopper's evidence upload is NOT shaped like this (see `EvidenceUploadResponseSchema`);
 * this one stays two-step because it is the merchant's session that authorises it rather
 * than a rate limit alone.
 */
export const LogoUploadRequestSchema = z.object({
  contentType: z.string().min(1),
  contentLength: z.number().int().positive(),
});
export type LogoUploadRequest = z.infer<typeof LogoUploadRequestSchema>;

export const LogoUploadResponseSchema = z.object({
  uploadId: UploadIdSchema,
  uploadUrl: z.string().min(1),
  expiresAt: IsoTimestampSchema,
});
export type LogoUploadResponse = z.infer<typeof LogoUploadResponseSchema>;

/**
 * The shopper's evidence photo, uploaded in ONE multipart request to
 * `POST /api/v1/returns/shopper/merchants/:merchantSlug/uploads` (SAI-1049). The bytes go
 * straight to the server under the shopper's session, so there is no presigned URL and no
 * expiry to hand back: the id is the whole answer, and it is the same pinned format we
 * accept back in `evidence[].uploadId` on submit.
 */
export const EvidenceUploadResponseSchema = z.object({
  uploadId: UploadIdSchema,
});
export type EvidenceUploadResponse = z.infer<
  typeof EvidenceUploadResponseSchema
>;

/**
 * A fresh, short-lived URL to view one evidence photo — minted on demand by
 * `GET /api/merchant/:merchantId/returns/:id/evidence/:evidenceId/view` (never stored;
 * see `services/create-return.ts` for why `photoUrl` itself is not fetchable).
 */
export const EvidenceViewResponseSchema = z.object({
  url: z.string().min(1),
  expiresAt: IsoTimestampSchema,
});
export type EvidenceViewResponse = z.infer<typeof EvidenceViewResponseSchema>;

/** Kept off the wire on purpose: `photoUrl` is a bucket key, not a URL a browser can load. */
export const EvidenceSummarySchema = z.object({
  id: z.string().min(1),
  returnLineId: z.string().min(1),
  note: z.string().nullable(),
  createdAt: IsoTimestampSchema,
});
export type EvidenceSummary = z.infer<typeof EvidenceSummarySchema>;

/**
 * One return in full, as both merchant surfaces answer it
 * (`/api/merchant/:merchantId/returns/:id` and `/api/settings/:merchantSlug/returns/:id`).
 * Working photo URLs are minted separately, on demand, by the `evidence/:id/view` route.
 */
export const ReturnDetailResponseSchema = z.object({
  order: ReturnOrderSchema,
  lines: z.array(ReturnLineSchema),
  evidence: z.array(EvidenceSummarySchema),
});
export type ReturnDetailResponse = z.infer<typeof ReturnDetailResponseSchema>;

/**
 * `GET .../returns/:id/events`: the append-only log for one return, oldest first, in full.
 * The merchant owns the return, so nothing is redacted here; the shopper's projection is
 * `ShopperEventSchema`.
 */
export const ReturnEventsResponseSchema = z.object({
  events: z.array(ReturnEventSchema),
});
export type ReturnEventsResponse = z.infer<typeof ReturnEventsResponseSchema>;

export const TrackRequestSchema = z.object({
  merchantSlug: z.string().min(1),
  reference: ReturnReferenceSchema,
  phone: PhoneSchema,
});
export type TrackRequest = z.infer<typeof TrackRequestSchema>;

export const TrackedLineSchema = z.object({
  lineRef: z.string().min(1),
  productName: LocalizedTextSchema,
  state: LineStateSchema,
  /** Exposed only once the line is `inspected` (docs/06). */
  grade: GradeSchema.optional(),
  /** Renders to the shopper in full whenever state is `rejected`. */
  rejectionReason: LocalizedTextSchema.optional(),
  qtyExpected: z.number().int().positive(),
  qtyReceived: z.number().int().nonnegative().nullable(),
});
export type TrackedLine = z.infer<typeof TrackedLineSchema>;

export const TrackResponseSchema = z.object({
  reference: ReturnReferenceSchema,
  lines: z.array(TrackedLineSchema),
  /** State steps only. Kept for callers that predate `events`; derived from the same log. */
  timeline: z.array(TimelineStepSchema),
  /**
   * The return's event log, filtered to what a shopper may see and ordered oldest first.
   * The same rows the merchant reads on `GET /returns/:id/events`, projected through
   * `ShopperEventSchema`.
   */
  events: z.array(ShopperEventSchema),
});
export type TrackResponse = z.infer<typeof TrackResponseSchema>;

export const CancelReturnRequestSchema = z.object({
  merchantSlug: z.string().min(1),
  phone: PhoneSchema,
  /**
   * Which return to cancel. Also present in the path
   * (`POST /api/public/returns/:reference/cancel`), and the handler must treat the PATH as
   * authoritative and refuse a body that disagrees — a body that could redirect the
   * cancellation elsewhere is worse than no body field at all.
   *
   * Optional ONLY for now: existing callers put the reference solely in the path, and this
   * package is being compiled against live. It SHOULD become required once every caller
   * populates it, so the schema alone documents what a cancel identifies.
   */
  reference: ReturnReferenceSchema.optional(),
});
export type CancelReturnRequest = z.infer<typeof CancelReturnRequestSchema>;

export const CancelReturnResponseSchema = z.object({
  reference: ReturnReferenceSchema,
  cancelled: z.boolean(),
});
export type CancelReturnResponse = z.infer<typeof CancelReturnResponseSchema>;

/* ------------------------------------------------------------------ */
/* Merchant — called service-to-service by the Sirdab portal           */
/* ------------------------------------------------------------------ */

export const GetPolicyResponseSchema = z.object({
  policy: MerchantPolicySchema,
});
export type GetPolicyResponse = z.infer<typeof GetPolicyResponseSchema>;

/** 422 with violations when the submitted policy falls below the legal floor (TODO(decision):Q1). */
export const PutPolicyRequestSchema = z.object({
  policy: MerchantPolicySchema,
});
export type PutPolicyRequest = z.infer<typeof PutPolicyRequestSchema>;

/** Admin merchant creation (docs/06). Policy is validated by MerchantPolicySchema. */
export const CreateMerchantRequestSchema = z.object({
  slug: z
    .string()
    .min(1)
    .regex(
      /^[a-z0-9][a-z0-9-]*$/,
      "slug must be lowercase alphanumerics and hyphens",
    ),
  merchantId: z
    .string()
    .min(1)
    .regex(
      /^[a-z0-9_-]+$/,
      "merchantId must be lowercase alphanumerics, underscores and hyphens",
    ),
  displayName: LocalizedTextSchema,
  policy: MerchantPolicySchema,
});
export type CreateMerchantRequest = z.infer<typeof CreateMerchantRequestSchema>;

/**
 * `state` matches a return with at least one line in that exact state, the integration
 * filter that has always been here. `group` matches on the return's DERIVED header state
 * (docs/04) mapped through `schemas/state-group.ts`, which is what the merchant's Returns
 * tab filters on: one word per return, the same word the row is labelled with. `sort`
 * defaults per surface (`routes/settings.ts` says why), so it is optional here.
 */
export const ListReturnsQuerySchema = PaginationQuerySchema.extend({
  state: LineStateSchema.optional(),
  group: MerchantStateGroupSchema.optional(),
  sort: ListReturnsSortSchema.optional(),
});
export type ListReturnsQuery = z.infer<typeof ListReturnsQuerySchema>;

export const ListReturnsResponseSchema = z.object({
  returns: z.array(
    z.object({
      order: ReturnOrderSchema,
      lines: z.array(ReturnLineSchema),
      /**
       * The merchant-facing group of the return's derived header state, computed once on
       * the server so the label on a row and the filter that found it come from the same
       * function.
       */
      stateGroup: MerchantStateGroupSchema,
    }),
  ),
  nextCursor: z.string().nullable(),
});
export type ListReturnsResponse = z.infer<typeof ListReturnsResponseSchema>;

export const ApproveLinesRequestSchema = z.object({
  lineIds: z.array(z.string().min(1)).min(1),
  note: z.string().optional(),
});
export type ApproveLinesRequest = z.infer<typeof ApproveLinesRequestSchema>;

/** `reasonText` is required at the schema level — reject without it is a 422 (docs/06). */
export const RejectLinesRequestSchema = z.object({
  lineIds: z.array(z.string().min(1)).min(1),
  reasonText: LocalizedTextSchema,
});
export type RejectLinesRequest = z.infer<typeof RejectLinesRequestSchema>;

export const LineStateChangeResponseSchema = z.object({
  lines: z.array(
    z.object({ lineId: z.string().min(1), state: LineStateSchema }),
  ),
});
export type LineStateChangeResponse = z.infer<
  typeof LineStateChangeResponseSchema
>;

/* ------------------------------------------------------------------ */
/* Internal — warehouse, driven by the already-shipped V2 screens      */
/* ------------------------------------------------------------------ */

export const ResolveScanRequestSchema = z
  .object({
    reference: ReturnReferenceSchema.optional(),
    awb: z.string().min(1).optional(),
  })
  .refine((v) => Boolean(v.reference) || Boolean(v.awb), {
    message: "either reference or awb is required",
  });
export type ResolveScanRequest = z.infer<typeof ResolveScanRequestSchema>;

/**
 * TODO(decision):Q6 — What happens to an unreferenced parcel at the dock (accept and hold
 * as unidentified, or refuse) is undecided. The union keeps both open: the response only
 * reports the fact, and carries no downstream instruction.
 */
export const ResolveScanResponseSchema = z.union([
  z.object({
    returnId: z.string().min(1),
    reference: ReturnReferenceSchema,
    expectedLines: z.array(
      z.object({
        lineId: z.string().min(1),
        productName: LocalizedTextSchema,
        qtyExpected: z.number().int().positive(),
      }),
    ),
  }),
  z.object({ unreferenced: z.literal(true) }),
]);
export type ResolveScanResponse = z.infer<typeof ResolveScanResponseSchema>;

/** Records `qty_received` ALONGSIDE `qty_expected`. Never overwrites it. */
export const ReceiveRequestSchema = z.object({
  lines: z
    .array(
      z.object({
        lineId: z.string().min(1),
        qtyReceived: z.number().int().nonnegative(),
      }),
    )
    .min(1),
});
export type ReceiveRequest = z.infer<typeof ReceiveRequestSchema>;

/** A mismatch is a successful response with a discrepancy flag — not an error (docs/06). */
export const ReceiveResponseSchema = z.object({
  lines: z.array(
    z.object({
      lineId: z.string().min(1),
      qtyExpected: z.number().int().positive(),
      qtyReceived: z.number().int().nonnegative(),
      discrepancy: z.boolean(),
      state: LineStateSchema,
    }),
  ),
});
export type ReceiveResponse = z.infer<typeof ReceiveResponseSchema>;

export const GradeRequestSchema = z.object({
  lines: z
    .array(
      z.object({
        lineId: z.string().min(1),
        grade: GradeSchema,
        disposition: DispositionSchema,
        photoUrls: z.array(z.string().min(1)),
      }),
    )
    .min(1),
});
export type GradeRequest = z.infer<typeof GradeRequestSchema>;

export const GradeResponseSchema = LineStateChangeResponseSchema;
export type GradeResponse = z.infer<typeof GradeResponseSchema>;

/* ------------------------------------------------------------------ */
/* Outbound events emitted to the merchant                             */
/* ------------------------------------------------------------------ */

export const OutboundEventTypeSchema = z.enum([
  "return.created",
  "return.received",
  "return.graded",
  "return.restocked",
  "return.resolved",
]);
export type OutboundEventType = z.infer<typeof OutboundEventTypeSchema>;

/**
 * `return.resolved` carries credit-note-READY line data — original invoice reference,
 * line values, VAT split. We never issue the document (ADR-004).
 */
export const CreditNoteLineDataSchema = z.object({
  lineId: z.string().min(1),
  sirdabSkuId: z.string().nullable(),
  qty: z.number().int().nonnegative(),
  unitPrice: MoneyHalalasSchema,
  vatAmount: MoneyHalalasSchema,
});
export type CreditNoteLineData = z.infer<typeof CreditNoteLineDataSchema>;

export const OutboundEventSchema = z.object({
  /** Idempotency key for at-least-once delivery. */
  eventId: z.string().min(1),
  type: OutboundEventTypeSchema,
  merchantId: z.string().min(1),
  reference: ReturnReferenceSchema,
  occurredAt: IsoTimestampSchema,
  payload: z.record(z.string(), z.unknown()),
});
export type OutboundEvent = z.infer<typeof OutboundEventSchema>;

/* ------------------------------------------------------------------ */
/* Merchant settings surface (Tier-1 token link)                       */
/* ------------------------------------------------------------------ */

/**
 * Extending the settings link pushes its expiry out. It deliberately does NOT rotate
 * the token: the merchant is almost always looking at the page when they press it, and
 * a rotation would kill the very tab they are working in.
 */
export const ExtendSettingsLinkResponseSchema = z.object({
  expiresAt: IsoTimestampSchema,
});
export type ExtendSettingsLinkResponse = z.infer<
  typeof ExtendSettingsLinkResponseSchema
>;

/**
 * Reading the link's expiry, unlike extending it, can legitimately answer "never".
 * Nothing that mints a settings link sets an expiry today, so `null` is the common case
 * and means the link does not expire — not that it is broken. Extending always produces
 * a timestamp, which is why only this shape is nullable.
 */
export const GetSettingsLinkResponseSchema = z.object({
  expiresAt: IsoTimestampSchema.nullable(),
});
export type GetSettingsLinkResponse = z.infer<
  typeof GetSettingsLinkResponseSchema
>;

// (EvidenceViewResponseSchema is already defined above for the merchant surface;
//  the settings surface mints the same shape.)

export const SettingsStatsReasonSchema = z.object({
  reasonCode: z.string().min(1),
  count: z.number().int().nonnegative(),
  /** 0..1 of all returns in the window. */
  share: z.number().min(0).max(1),
});
export type SettingsStatsReason = z.infer<typeof SettingsStatsReasonSchema>;

export const SettingsStatsResponseSchema = z.object({
  counts: z.object({
    waitingOnYou: z.number().int().nonnegative(),
    inTransit: z.number().int().nonnegative(),
    resolved: z.number().int().nonnegative(),
    totalThisMonth: z.number().int().nonnegative(),
  }),
  /** Descending by count. */
  reasons: z.array(SettingsStatsReasonSchema),
  money: z.object({
    returnedValueHalalas: MoneyHalalasSchema,
    feesCollectedHalalas: MoneyHalalasSchema,
    currency: z.literal("SAR"),
  }),
  /**
   * Returns as a share of orders. Always `null` until a real Sirdab adapter can supply
   * total order count — the fixture adapter cannot, and a fabricated percentage on a
   * merchant's dashboard is worse than an honest blank.
   */
  returnRate: z.number().min(0).max(1).nullable(),
});
export type SettingsStatsResponse = z.infer<typeof SettingsStatsResponseSchema>;
